/** Manrope is the approved Beauty Burst UI typeface. */
export const fontFamily = {
  ui: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
  display: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

/** Type scale: [font-size, line-height, letter-spacing] */
export const fontSize = {
  xs: ['0.75rem', '1.125rem', '0.01em'],
  sm: ['0.875rem', '1.3125rem', '0.005em'],
  base: ['1rem', '1.5rem', '0em'],
  md: ['1.125rem', '1.6875rem', '0em'],
  lg: ['1.25rem', '1.75rem', '-0.005em'],
  xl: ['1.5rem', '2rem', '-0.01em'],
  '2xl': ['1.875rem', '2.375rem', '-0.015em'],
  '3xl': ['2.25rem', '2.75rem', '-0.02em'],
  '4xl': ['3rem', '3.375rem', '-0.02em'],
} as const;

export type FontSizeToken = keyof typeof fontSize;

export const textStyle = {
  displayXl: { size: '4xl', weight: fontWeight.extrabold, family: fontFamily.display },
  displayLg: { size: '3xl', weight: fontWeight.bold, family: fontFamily.display },
  headingLg: { size: '2xl', weight: fontWeight.bold, family: fontFamily.ui },
  headingMd: { size: 'xl', weight: fontWeight.semibold, family: fontFamily.ui },
  headingSm: { size: 'lg', weight: fontWeight.semibold, family: fontFamily.ui },
  bodyLg: { size: 'md', weight: fontWeight.regular, family: fontFamily.ui },
  body: { size: 'base', weight: fontWeight.regular, family: fontFamily.ui },
  bodySm: { size: 'sm', weight: fontWeight.regular, family: fontFamily.ui },
  label: { size: 'sm', weight: fontWeight.semibold, family: fontFamily.ui },
  caption: { size: 'xs', weight: fontWeight.medium, family: fontFamily.ui },
  price: { size: 'lg', weight: fontWeight.bold, family: fontFamily.ui },
  tableCell: { size: 'sm', weight: fontWeight.regular, family: fontFamily.ui },
  numeric: { size: 'sm', weight: fontWeight.semibold, family: fontFamily.mono },
} as const;

export type TextStyleToken = keyof typeof textStyle;

/**
 * Resolves a semantic text style token into concrete CSS declarations.
 * Applications must use this rather than hard coding font sizes so the scale
 * stays in one place.
 */
export function typeStyle(token: TextStyleToken): {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
} {
  const style = textStyle[token];
  const [size, lineHeight, letterSpacing] = fontSize[style.size as FontSizeToken];
  return {
    fontFamily: style.family,
    fontWeight: style.weight,
    fontSize: size,
    lineHeight,
    letterSpacing,
  };
}
